import { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { showSnackbar } from "@/lib/snackbarUtils";
import { CANCEL_EVENT_REGISTRATION_MUTATION } from "../queries";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import EmailPreviewDialog, {
  EMAIL_PREVIEW_MODAL_CLASSNAME,
  EmailPreviewModalActions
} from "@/components/EmailPreviewDialog";
import { useCanceledEventRegistrationEmailPreview } from "@/pages/events/_hooks/useCanceledEventRegistrationEmailPreview";
import { OrderStatus } from "../types";
import { DateTime } from "luxon";
import Checkbox from "@/components/Checkbox";
import TextField from "@/components/TextField";
import { Controller, useForm, useWatch } from "react-hook-form";
import { NumberFormatBase } from "react-number-format";
import { centsToDollarsFormatted, currencyFormatter } from "@/lib/currencyHelpers";
import { useOutletContext, useParams } from "react-router-dom";
import type { EventOutletContext } from "../(tabbed)/_layout";
import useAmplitude from "@/hooks/useAmplitude";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";

interface CancelModalProps {
  event: {
    id?: string;
    waitlistEnabled?: boolean;
    startsAt?: string;
    eventType?: string | { name: string };
  };
  eventRegistrationId: string;
  open: boolean;
  setOpen: (value: boolean) => void;
  onError: (errorMessage: string) => void;
  order?: { status: OrderStatus; totalPaid?: number };
  refetch?: () => void;
}

export default function CancelModal({
  event,
  order,
  open,
  setOpen,
  onError,
  eventRegistrationId,
  refetch
}: CancelModalProps) {
  const totalPaidCents = order?.totalPaid ?? null;
  const totalPaidLabel = centsToDollarsFormatted(totalPaidCents);
  const outletContext = useOutletContext<EventOutletContext | undefined>();
  const { eventSlug = "" } = useParams();
  const { trackEvent } = useAmplitude();
  const eventId = event.id ?? outletContext?.eventData?.id;

  const defaultValues = {
    issueRefund: false,
    suspend: false,
    refundAmountCents: totalPaidCents ?? 0
  };
  type FieldValues = typeof defaultValues;

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm({ defaultValues, mode: "onChange" });
  const issueRefund = useWatch({ control, name: "issueRefund" });

  const [cancelEventRegistration, { loading: mutationLoading }] = useMutation(CANCEL_EVENT_REGISTRATION_MUTATION);
  const [previewOpen, setPreviewOpen] = useState(false);
  const {
    loadPreview,
    resetPreview,
    preview,
    loading: previewLoading,
    error: previewError
  } = useCanceledEventRegistrationEmailPreview(eventRegistrationId);

  useEffect(() => {
    if (!open) {
      setPreviewOpen(false);
      resetPreview();
    }
  }, [open, resetPreview]);

  const handlePreviewEmail = () => {
    setPreviewOpen(true);
    void loadPreview();
  };

  const hasRefundAmountError = Boolean(errors.refundAmountCents);
  const submitDisabled = mutationLoading || (issueRefund && hasRefundAmountError);

  const diffHours = event?.startsAt ? DateTime.fromISO(event.startsAt).diffNow("hours").hours : null;
  const eventTypeName = typeof event?.eventType === "string" ? event.eventType : event?.eventType?.name;
  const shouldSuspend = eventTypeName === "PAT" && diffHours !== null && diffHours <= 72;
  const refundable = order && order.status === OrderStatus.PAID;

  const handleCancel = handleSubmit(async (fields: FieldValues) => {
    try {
      let refundAmountCents: number | undefined;
      if (fields.issueRefund && totalPaidCents != null && fields.refundAmountCents < totalPaidCents) {
        refundAmountCents = fields.refundAmountCents;
      }

      const variables = {
        id: eventRegistrationId,
        suspend: fields.suspend,
        issueRefund: fields.issueRefund,
        ...(refundAmountCents !== undefined && { refundAmountCents })
      };

      const { data } = await cancelEventRegistration({ variables });
      const { success, message } = data.cancelEventRegistration;
      if (success) {
        refetch && refetch();
        showSnackbar("Registration canceled", { close: true });
        setOpen(false);
        trackEvent(
          EVENT_ANALYTICS_EVENTS.registrationCanceled,
          getEventAnalyticsData({
            source: "registrations",
            eventId,
            eventSlug,
            registrationId: eventRegistrationId,
            action: "cancel",
            issueRefund: fields.issueRefund,
            suspend: fields.suspend
          })
        );
      } else {
        onError(`The registration could not be canceled. Error: ${message}`);
      }
    } catch (e) {
      onError((e as Error).message);
    }
  });

  const text = [
    "Are you sure you want to cancel this person's registration to this event?",
    refundable && !shouldSuspend && "Select “Refund Payor” to have the registration payment refunded.",
    event.waitlistEnabled &&
      "All those who have signed up for event notifications will also be notified that a spot is now available.",
    shouldSuspend &&
      "Because this is a PAT cancellation with less than 72 hours notice, this person will be suspended for 90 days from PAT registrations."
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <Dialog
        open={open}
        onClosed={() => setOpen(false)}
        headline="Cancel Registration"
        className={`${EMAIL_PREVIEW_MODAL_CLASSNAME} max-w-[560px] p-6 pr-2`}
        hasFormElements={false}
        actions={
          <EmailPreviewModalActions onPreview={handlePreviewEmail} previewDisabled={mutationLoading}>
            <Button variant="text" onClick={() => setOpen(false)}>
              Go Back
            </Button>
            <Button
              variant="filled"
              disabled={submitDisabled}
              onClick={handleCancel}
              testId="cancel-registration-button"
            >
              Cancel Registration
            </Button>
          </EmailPreviewModalActions>
        }
      >
        <div tabIndex={0} className="flex items-center text-sys-brand-on-surface-variant pb-2">
          <p className="text-body-medium">{text}</p>
        </div>
        {shouldSuspend && (
          <div className="">
            <div className="mt-6">
              <Controller
                control={control}
                name="suspend"
                render={({ field: { onChange, value } }) => (
                  <Checkbox
                    title="Suspend Registrant"
                    label="If checked, registrant will be suspended for 90 days"
                    checked={value}
                    onChange={onChange}
                    name="suspend-registrant"
                  />
                )}
              />
            </div>
          </div>
        )}
        {refundable && (
          <div className="mt-6">
            <Controller
              control={control}
              name="issueRefund"
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  title="Refund Payor"
                  label="If checked, payor will be refunded the registration"
                  checked={value}
                  onChange={onChange}
                  name="refund-payor"
                />
              )}
            />
            {issueRefund && totalPaidCents != null && (
              <div className="mt-4">
                <Controller
                  control={control}
                  name="refundAmountCents"
                  rules={{
                    validate: (value) => {
                      if (!value) return "Refund amount is required";
                      if (value > totalPaidCents) return `Refund amount cannot exceed ${totalPaidLabel}`;
                      return true;
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <NumberFormatBase
                      className="w-full"
                      value={field.value}
                      format={currencyFormatter}
                      customInput={TextField}
                      label="Refund Amount"
                      leadingIcon="attach_money"
                      supportingText={`Net Payment Amount: ${totalPaidLabel}`}
                      required
                      error={!!fieldState.error}
                      errorText={fieldState.error?.message}
                      onFocus={(e) => e.target.select()}
                      onValueChange={(values) => {
                        field.onChange(Math.round(values.floatValue ?? 0));
                      }}
                    />
                  )}
                />
              </div>
            )}
          </div>
        )}
      </Dialog>
      <EmailPreviewDialog
        open={previewOpen}
        onClosed={() => setPreviewOpen(false)}
        subject={previewLoading ? undefined : preview?.subject}
        html={previewLoading ? undefined : preview?.html}
        loading={previewLoading}
        errorMessage={previewLoading ? null : (previewError?.message ?? null)}
      />
    </>
  );
}
